import {
  useMutation,
  useQueryClient,
  useSuspenseQuery,
} from "@tanstack/react-query";

import { useViewerHasPermission } from "@/auth/shared";
import { Link } from "@/components/base/link";
import { useTRPC } from "@/integrations/trpc/react";

import { EditableImage } from "../base/editable-image";
import { ReorderVenuesForm } from "./reorder-venues-form";

export function VenuesList() {
  const trpc = useTRPC();
  const queryClient = useQueryClient();

  const { data: venues } = useSuspenseQuery(trpc.venues.list.queryOptions());

  const { mutate: updateVenue } = useMutation({
    ...trpc.venues.updateContent.mutationOptions(),
    onSuccess: () => {
      queryClient.invalidateQueries(trpc.venues.pathFilter());
    },
  });

  const canCreateVenue = useViewerHasPermission({
    venues: ["create"],
  });

  return (
    <div className={"m-auto flex flex-col space-y-12 py-8"}>
      <div className="flex flex-col space-y-3 px-2 md:px-8">
        <div className="flex flex-row items-center justify-between">
          <h2 className="mb-8 text-4xl font-bold">
            Find
            <br /> Tournaments
          </h2>

          {canCreateVenue && <ReorderVenuesForm />}
        </div>

        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 md:grid-cols-3 md:gap-6">
          {venues?.map(({ id, name, city, thumbnailImageSource }) => (
            <div
              key={id}
              className="relative overflow-hidden rounded-lg bg-white shadow-sm"
            >
              <Link
                to={"/venues/$venueId"}
                params={{ venueId: id.toString() }}
                className="hover:underline"
              >
                {thumbnailImageSource && (
                  <img
                    className="max-h-80 min-h-80 w-full object-cover"
                    src={thumbnailImageSource}
                    alt={`${name}, ${city}`}
                  />
                )}

                <div className="text-navy-500 font-panton bg-white px-4 py-3 text-2xl font-extrabold uppercase">
                  {name},<br />
                  {city}
                </div>
              </Link>
              <EditableImage
                editable={canCreateVenue}
                source={thumbnailImageSource ?? ""}
                bucket="venues"
                prefix={`${id}/thumbnails`}
                onUploadSuccess={(source) => {
                  queryClient.setQueriesData(
                    trpc.venues.list.queryOptions(),
                    (data) => {
                      return data.map((venue) =>
                        venue.id === id
                          ? { ...venue, thumbnailImageSource: source }
                          : venue,
                      );
                    },
                  );
                }}
                onDiscard={(original) => {
                  queryClient.setQueriesData(
                    trpc.venues.list.queryOptions(),
                    (data) => {
                      return data.map((venue) =>
                        venue.id === id
                          ? { ...venue, thumbnailImageSource: original }
                          : venue,
                      );
                    },
                  );
                }}
                onSave={(source) => {
                  updateVenue({
                    id,
                    thumbnailImageSource: source,
                  });
                }}
              />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
